import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Flame, Beef, Wheat, Droplets, Plus, Trash2, Check, ChevronLeft, ChevronRight, Target, TrendingUp, X, CalendarDays, LogIn, Utensils, ArrowRight, Zap, Award } from 'lucide-react';
import { macrosApi, mealPlansApi, macrosExtraApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const SLOTS = [
  { id: 'breakfast', label: 'Breakfast', color: '#f59e0b', icon: '1' },
  { id: 'lunch', label: 'Lunch', color: '#22c55e', icon: '2' },
  { id: 'dinner', label: 'Dinner', color: '#E63946', icon: '3' },
  { id: 'snack', label: 'Snack', color: '#8b5cf6', icon: '4' },
];

const RANGES = [
  { id: 'daily', label: 'Today' },
  { id: 'weekly', label: '7 Days' },
  { id: 'monthly', label: '30 Days' },
  { id: 'all', label: 'All Time' },
];

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function ComplianceRing({ percent, size = 80, strokeWidth = 6, color = '#22c55e', label }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#27272a" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <span className="absolute text-sm font-heading text-white" style={{ lineHeight: `${size}px` }}>{percent}%</span>
      {label && <span className="text-[10px] text-zinc-500 font-mono uppercase">{label}</span>}
    </div>
  );
}

function QuickLogFromPlan({ planMeals, recipes, customRecipes, onLog, slot }) {
  const allRecipesMap = {};
  recipes.forEach(r => { allRecipesMap[r.id] = r; });
  customRecipes.forEach(r => { allRecipesMap[r.id] = r; });

  const slotMeals = planMeals.filter(m => m.slot === slot);
  if (slotMeals.length === 0) return null;

  return (
    <div className="space-y-1">
      {slotMeals.map((meal) => {
        const rid = meal.custom_recipe_id || meal.recipe_id;
        const recipe = allRecipesMap[rid];
        if (!recipe) return null;
        const servings = meal.servings || 1;
        return (
          <button key={meal.id || rid} onClick={() => onLog({
            name: recipe.name,
            protein: recipe.protein * servings,
            calories: recipe.calories * servings,
            carbs: Math.round((recipe.calories * servings * 0.4) / 4),
            fat: Math.round((recipe.calories * servings * 0.3) / 9),
            source: 'plan',
            recipe_id: rid,
            slot,
            servings,
          })}
            className="w-full flex items-center gap-2 px-3 py-2 border border-zinc-800/50 bg-zinc-900/30 hover:border-[#22c55e]/50 hover:bg-[#22c55e]/5 transition-all text-left"
            data-testid={`quick-log-${rid}`}>
            <Check className="w-3 h-3 text-zinc-600" />
            <span className="text-sm text-zinc-300 flex-1 truncate">{recipe.name}</span>
            <span className="text-[10px] text-zinc-600 font-mono">{recipe.protein * servings}g P / {recipe.calories * servings} cal</span>
          </button>
        );
      })}
    </div>
  );
}

function ManualEntryForm({ onLog, onClose, slot }) {
  const [form, setForm] = useState({ name: '', calories: '', protein: '', carbs: '', fat: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onLog({
      name: form.name.trim(),
      protein: parseFloat(form.protein) || 0,
      calories: parseFloat(form.calories) || 0,
      carbs: parseFloat(form.carbs) || 0,
      fat: parseFloat(form.fat) || 0,
      source: 'manual',
      recipe_id: null,
      slot,
      servings: 1,
    });
    onClose();
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/60 p-4 space-y-3" data-testid="manual-entry-form">
      <div className="flex items-center justify-between">
        <p className="text-xs font-heading uppercase tracking-wider text-zinc-500">Manual Entry</p>
        <button onClick={onClose} className="text-zinc-600 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Food name"
        className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e]"
        data-testid="manual-food-name" />
      <div className="grid grid-cols-4 gap-2">
        <div>
          <label className="text-[9px] text-zinc-600 font-mono">Calories</label>
          <input value={form.calories} onChange={e => set('calories', e.target.value)} type="number" placeholder="0"
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2 outline-none focus:border-[#f59e0b]"
            data-testid="manual-calories" />
        </div>
        <div>
          <label className="text-[9px] text-zinc-600 font-mono">Protein</label>
          <input value={form.protein} onChange={e => set('protein', e.target.value)} type="number" placeholder="0"
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2 outline-none focus:border-[#22c55e]"
            data-testid="manual-protein" />
        </div>
        <div>
          <label className="text-[9px] text-zinc-600 font-mono">Carbs</label>
          <input value={form.carbs} onChange={e => set('carbs', e.target.value)} type="number" placeholder="0"
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2 outline-none focus:border-[#3b82f6]"
            data-testid="manual-carbs" />
        </div>
        <div>
          <label className="text-[9px] text-zinc-600 font-mono">Fat</label>
          <input value={form.fat} onChange={e => set('fat', e.target.value)} type="number" placeholder="0"
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2 outline-none focus:border-[#E63946]"
            data-testid="manual-fat" />
        </div>
      </div>
      <button onClick={handleSubmit}
        className="w-full py-2 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors"
        data-testid="manual-submit">Log Meal</button>
    </div>
  );
}

function TargetsModal({ targets, onSave, onClose }) {
  const [form, setForm] = useState(targets);
  const set = (k, v) => setForm(p => ({ ...p, [k]: parseInt(v) || 0 }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#111] border border-zinc-800 w-full max-w-sm p-5" onClick={e => e.stopPropagation()} data-testid="targets-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider">Daily Targets</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          {[
            { key: 'calories', label: 'Calories', color: '#f59e0b', unit: 'cal' },
            { key: 'protein', label: 'Protein', color: '#22c55e', unit: 'g' },
            { key: 'carbs', label: 'Carbs', color: '#3b82f6', unit: 'g' },
            { key: 'fat', label: 'Fat', color: '#E63946', unit: 'g' },
          ].map(t => (
            <div key={t.key} className="flex items-center gap-3">
              <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
              <span className="text-sm text-zinc-400 w-16">{t.label}</span>
              <input value={form[t.key]} onChange={e => set(t.key, e.target.value)} type="number"
                className="flex-1 bg-zinc-900 border border-zinc-800 text-white text-sm p-2 outline-none focus:border-zinc-600 text-right"
                data-testid={`target-${t.key}`} />
              <span className="text-[10px] text-zinc-600 w-6">{t.unit}</span>
            </div>
          ))}
        </div>
        <button onClick={() => { onSave(form); onClose(); }}
          className="w-full mt-4 py-2.5 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors"
          data-testid="save-targets-btn">Save Targets</button>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#111] border border-zinc-800 p-3 text-xs">
      <p className="text-zinc-400 font-mono mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-bold">{p.name}: {Math.round(p.value)}{p.name === 'Calories' ? '' : 'g'}</p>
      ))}
    </div>
  );
};

export default function MacroTrackerPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [logs, setLogs] = useState([]);
  const [targets, setTargets] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 67 });
  const [compliance, setCompliance] = useState(null);
  const [chartRange, setChartRange] = useState('weekly');
  const [summaryData, setSummaryData] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null); // for manual entry
  const [showTargetsModal, setShowTargetsModal] = useState(false);
  const [planMeals, setPlanMeals] = useState([]);
  const [planRecipes, setPlanRecipes] = useState([]);
  const [planCustomRecipes, setPlanCustomRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);

  // Get day name for meal plan lookup
  const dayName = new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const [logsRes, targetsRes, compRes, summRes, streakRes] = await Promise.all([
        macrosApi.getLogs(selectedDate, token),
        macrosApi.getTargets(token),
        macrosApi.getCompliance(selectedDate, token),
        macrosApi.getSummary(chartRange, token),
        macrosExtraApi.getStreak(token).catch(() => ({ data: null })),
      ]);
      setLogs(logsRes.data || []);
      setTargets(targetsRes.data || { calories: 2000, protein: 150, carbs: 200, fat: 67 });
      setCompliance(compRes.data || null);
      setSummaryData(summRes.data || null);
      if (streakRes.data) setStreak(streakRes.data);
    } catch (err) {
      console.error('Load macro data failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, chartRange]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load the user's current meal plan for quick-log
  useEffect(() => {
    const loadPlan = async () => {
      try {
        const { data } = await mealPlansApi.getDefaultPlan();
        const dayMeals = data.days?.[dayName] || [];
        setPlanMeals(dayMeals);
        setPlanRecipes(Object.values(data.recipes || {}));
      } catch (err) {
        console.error('Load plan failed:', err);
      }
    };
    loadPlan();
  }, [dayName]);

  // Also load saved plan if logged in
  useEffect(() => {
    if (!token) return;
    mealPlansApi.getAll(token).then(async (res) => {
      const plans = res.data || [];
      if (plans.length > 0) {
        const { data } = await mealPlansApi.getById(plans[0].id, token);
        const dayMeals = data.days?.[dayName] || [];
        setPlanMeals(dayMeals);
        setPlanCustomRecipes(data.custom_recipes || []);
      }
    }).catch(() => {});
  }, [token, dayName]);

  const handleLog = async (entry) => {
    if (!token) return;
    try {
      await macrosApi.log({ ...entry, date: selectedDate }, token);
      await loadData();
    } catch (err) {
      console.error('Log failed:', err);
    }
  };

  const handleDeleteLog = async (id) => {
    if (!token) return;
    try {
      await macrosApi.deleteLog(id, token);
      await loadData();
    } catch (err) {
      console.error('Delete log failed:', err);
    }
  };

  const handleSaveTargets = async (newTargets) => {
    if (!token) return;
    try {
      await macrosApi.setTargets(newTargets, token);
      setTargets(newTargets);
      await loadData();
    } catch (err) {
      console.error('Save targets failed:', err);
    }
  };

  const changeDate = (delta) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  // Logged totals for today
  const logged = logs.reduce((acc, l) => ({
    calories: acc.calories + (l.calories || 0),
    protein: acc.protein + (l.protein || 0),
    carbs: acc.carbs + (l.carbs || 0),
    fat: acc.fat + (l.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  // Chart data formatting
  const chartData = (summaryData?.days || []).map(d => ({
    date: d.date.slice(5),
    Calories: d.calories,
    Protein: d.protein,
    Carbs: d.carbs,
    Fat: d.fat,
  }));

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-24 flex items-center justify-center" data-testid="macro-tracker-auth">
        <div className="text-center">
          <Target className="w-10 h-10 text-[#22c55e] mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-white mb-2">Macro Tracker</h2>
          <p className="text-zinc-500 text-sm mb-6">Log in to track your daily macros and see your progress.</p>
          <Link to="/auth" className="inline-flex items-center gap-2 px-6 py-3 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors" data-testid="login-to-track">
            <LogIn className="w-4 h-4" /> Log In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-zinc-500 text-sm font-mono">Loading macro tracker...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-300" data-testid="macro-tracker-page">
      {/* Header */}
      <section className="pt-24 pb-6 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-5 h-5 text-[#22c55e]" />
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-[#22c55e] border border-[#22c55e]/30 px-3 py-1">Macro Tracker</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl text-white">Daily Macros</h1>
              <p className="text-zinc-500 text-sm mt-1">Log meals, track compliance, and visualize your trends.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => setShowTargetsModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-zinc-600 hover:text-white transition-all"
                data-testid="set-targets-btn"><Target className="w-3 h-3" /> Targets</button>
              <Link to="/tools/meal-planner"
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-zinc-600 hover:text-white transition-all"
                data-testid="go-to-planner"><Utensils className="w-3 h-3" /> Planner</Link>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 pb-20">
        {/* Streak Banner */}
        {streak && (streak.current_streak > 0 || streak.longest_streak > 0) && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="streak-banner">
            <div className="border border-[#f59e0b]/30 bg-[#f59e0b]/5 p-4 flex items-center gap-3">
              <Flame className="w-6 h-6 text-[#f59e0b] flex-shrink-0" />
              <div>
                <p className="text-2xl font-heading text-white">{streak.current_streak}</p>
                <p className="text-[10px] font-mono text-[#f59e0b] uppercase tracking-wider">Day Streak</p>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-3">
              <Award className="w-6 h-6 text-[#00F0FF] flex-shrink-0" />
              <div>
                <p className="text-2xl font-heading text-white">{streak.longest_streak}</p>
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Best Streak</p>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-3">
              <CalendarDays className="w-6 h-6 text-[#22c55e] flex-shrink-0" />
              <div>
                <p className="text-2xl font-heading text-white">{streak.total_days_logged}</p>
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">Days Logged</p>
              </div>
            </div>
          </div>
        )}

        {/* Date nav */}
        <div className="flex items-center justify-between mb-6 border border-zinc-800 p-3" data-testid="date-nav">
          <button onClick={() => changeDate(-1)} className="p-1.5 text-zinc-500 hover:text-white transition-colors" data-testid="prev-day"><ChevronLeft className="w-5 h-5" /></button>
          <div className="text-center">
            <p className="text-white font-heading text-lg">{formatDate(selectedDate)}</p>
            <p className="text-[10px] text-zinc-600 font-mono">{selectedDate}</p>
          </div>
          <button onClick={() => changeDate(1)} className="p-1.5 text-zinc-500 hover:text-white transition-colors" data-testid="next-day"><ChevronRight className="w-5 h-5" /></button>
        </div>

        {/* Compliance overview */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="compliance-overview">
          {[
            { key: 'calories', label: 'Calories', icon: Flame, color: '#f59e0b', unit: '' },
            { key: 'protein', label: 'Protein', icon: Beef, color: '#22c55e', unit: 'g' },
            { key: 'carbs', label: 'Carbs', icon: Wheat, color: '#3b82f6', unit: 'g' },
            { key: 'fat', label: 'Fat', icon: Droplets, color: '#E63946', unit: 'g' },
          ].map(m => {
            const pct = compliance?.compliance?.[m.key] || 0;
            return (
              <div key={m.key} className="border border-zinc-800 p-4 relative overflow-hidden" data-testid={`compliance-${m.key}`}>
                <div className="absolute bottom-0 left-0 h-1 transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: m.color }} />
                <div className="flex items-center gap-1.5 mb-1">
                  <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                  <span className="text-[10px] font-heading uppercase tracking-wider text-zinc-500">{m.label}</span>
                </div>
                <p className="text-xl font-heading text-white">{Math.round(logged[m.key])}{m.unit}</p>
                <p className="text-[10px] font-mono text-zinc-600">/ {targets[m.key]}{m.unit} target</p>
                <p className="text-[10px] font-mono mt-0.5" style={{ color: pct >= 80 ? m.color : '#52525b' }}>{pct}%</p>
              </div>
            );
          })}
          {/* Overall compliance ring */}
          <div className="border border-zinc-800 p-4 flex flex-col items-center justify-center relative" data-testid="overall-compliance">
            <ComplianceRing percent={compliance?.overall_compliance || 0} size={64} color="#22c55e" />
            <span className="text-[10px] font-mono text-zinc-500 mt-2 uppercase">Overall</span>
          </div>
        </div>

        {/* Daily meal log by slot */}
        <div className="space-y-4 mb-10" data-testid="daily-log">
          {SLOTS.map(slot => {
            const slotLogs = logs.filter(l => l.slot === slot.id);
            return (
              <div key={slot.id} className="border border-zinc-800" data-testid={`slot-section-${slot.id}`}>
                <div className="flex items-center justify-between p-3 border-b border-zinc-800/50">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: slot.color }} />
                    <span className="text-xs font-heading uppercase tracking-wider text-white">{slot.label}</span>
                    {slotLogs.length > 0 && (
                      <span className="text-[10px] font-mono text-zinc-600">
                        {Math.round(slotLogs.reduce((s, l) => s + l.protein, 0))}g P /
                        {Math.round(slotLogs.reduce((s, l) => s + l.calories, 0))} cal
                      </span>
                    )}
                  </div>
                  <button onClick={() => setActiveSlot(activeSlot === slot.id ? null : slot.id)}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-500 hover:text-[#22c55e] transition-colors font-heading uppercase tracking-wider"
                    data-testid={`add-meal-${slot.id}`}>
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>

                {/* Logged entries */}
                {slotLogs.length > 0 && (
                  <div className="p-2 space-y-1">
                    {slotLogs.map(l => (
                      <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors" data-testid={`log-entry-${l.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{l.name}</p>
                          <div className="flex gap-3 text-[10px] text-zinc-600 font-mono">
                            <span>{l.calories} cal</span>
                            <span>{l.protein}g P</span>
                            <span>{l.carbs}g C</span>
                            <span>{l.fat}g F</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[9px] px-1.5 py-0.5 border ${l.source === 'plan' ? 'border-[#22c55e]/30 text-[#22c55e]' : 'border-zinc-800 text-zinc-600'}`}>
                            {l.source === 'plan' ? 'PLAN' : 'MANUAL'}
                          </span>
                          <button onClick={() => handleDeleteLog(l.id)} className="text-zinc-700 hover:text-red-500 transition-colors" data-testid={`delete-log-${l.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add meal section */}
                {activeSlot === slot.id && (
                  <div className="p-3 border-t border-zinc-800/50 space-y-3">
                    {/* Quick log from plan */}
                    {planMeals.length > 0 && (
                      <div>
                        <p className="text-[10px] font-heading uppercase tracking-widest text-zinc-600 mb-2">From Today's Plan</p>
                        <QuickLogFromPlan planMeals={planMeals} recipes={planRecipes} customRecipes={planCustomRecipes} onLog={handleLog} slot={slot.id} />
                      </div>
                    )}
                    {/* Manual entry */}
                    <ManualEntryForm onLog={handleLog} onClose={() => setActiveSlot(null)} slot={slot.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="border border-zinc-800 p-5 mb-8" data-testid="charts-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-white uppercase tracking-wider">Trends</h2>
            <div className="flex gap-1">
              {RANGES.map(r => (
                <button key={r.id} onClick={() => setChartRange(r.id)}
                  className={`px-3 py-1 text-[10px] font-heading uppercase tracking-wider border transition-all ${chartRange === r.id ? 'border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e]' : 'border-zinc-800 text-zinc-600 hover:text-zinc-400'}`}
                  data-testid={`range-${r.id}`}>{r.label}</button>
              ))}
            </div>
          </div>

          {/* Averages summary */}
          {summaryData && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-zinc-900/40 p-3 border border-zinc-800/30">
                <p className="text-lg font-heading text-white">{summaryData.averages?.calories || 0}</p>
                <p className="text-[10px] font-mono text-[#f59e0b]">AVG CALORIES</p>
              </div>
              <div className="bg-zinc-900/40 p-3 border border-zinc-800/30">
                <p className="text-lg font-heading text-white">{summaryData.averages?.protein || 0}g</p>
                <p className="text-[10px] font-mono text-[#22c55e]">AVG PROTEIN</p>
              </div>
              <div className="bg-zinc-900/40 p-3 border border-zinc-800/30">
                <p className="text-lg font-heading text-white">{summaryData.averages?.compliance || 0}%</p>
                <p className="text-[10px] font-mono text-[#3b82f6]">AVG COMPLIANCE</p>
              </div>
              <div className="bg-zinc-900/40 p-3 border border-zinc-800/30">
                <p className="text-lg font-heading text-white">{summaryData.total_days_logged || 0}</p>
                <p className="text-[10px] font-mono text-zinc-500">DAYS TRACKED</p>
              </div>
            </div>
          )}

          {/* Bar chart — daily macro breakdown */}
          {chartData.length > 0 ? (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Daily Breakdown</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" tick={{ fill: '#52525b', fontSize: 10 }} axisLine={{ stroke: '#27272a' }} />
                    <YAxis tick={{ fill: '#52525b', fontSize: 10 }} axisLine={{ stroke: '#27272a' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#52525b' }} />
                    <Bar dataKey="Protein" fill="#22c55e" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Carbs" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Fat" fill="#E63946" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Line chart — calorie trend */}
              <div>
                <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Calorie Trend</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" tick={{ fill: '#52525b', fontSize: 10 }} axisLine={{ stroke: '#27272a' }} />
                    <YAxis tick={{ fill: '#52525b', fontSize: 10 }} axisLine={{ stroke: '#27272a' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="Calories" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} />
                    <Line type="monotone" dataKey="Protein" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-600">
              <CalendarDays className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No data yet. Log some meals to see your trends.</p>
            </div>
          )}
        </div>

        {/* Link to meal planner */}
        <div className="border border-zinc-800 border-dashed p-6 text-center">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 mb-2">Need a Plan?</p>
          <Link to="/tools/meal-planner" className="inline-flex items-center gap-2 text-xs font-mono text-[#22c55e] hover:underline" data-testid="back-to-planner-link">
            Open Meal Planner <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </main>

      {/* Targets Modal */}
      {showTargetsModal && (
        <TargetsModal targets={targets} onSave={handleSaveTargets} onClose={() => setShowTargetsModal(false)} />
      )}
    </div>
  );
}
